'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Save, AlertCircle } from 'lucide-react'
import Dashboard from '../../components/dashboard/Dashboard'
import InputField from '../../components/ui/InputField'
import Section from '../../components/ui/Section'
import axios from 'axios'
import { ToastContainer, toast } from 'react-toastify';
import config from '../../config'
import { Country, State, City } from 'country-state-city';
import { ChevronDown, Plus, Trash2 } from 'lucide-react'
import { validationPatterns as newValidationPatterns, validateField as validateFieldUtil } from '../../utils/validationPatterns';
import ValidationErrorSidebar from '../../components/ui/ValidationErrorSidebar';
import { usePageSave } from '../../contexts/PageSaveContext'
import { useServerPageStatus } from '../../contexts/ServerPageStatusContext';

export default function ProfileForm() {
  const { markPageAsSaved, markPageAsUnsaved } = usePageSave()
  const { refreshAllPageStatuses } = useServerPageStatus()
  const [bankDetails, setBankDetails] = useState([{
    id: 1,
    IFSCCode: '',
    BankName: '',
    BankAccountNo: '',
    AccountType: '',
    useForRefund: false
  }])

  function generateSixDigitNumber() {
    return Math.floor(100000 + Math.random() * 900000);
  }


  // Update the addBank function
  const addBank = async () => {
    const lastBank = bankDetails[bankDetails.length - 1];
    if (bankDetails.length > 0 && (!lastBank || lastBank.IFSCCode === "" || lastBank.BankName === "" || lastBank.AccountType === "" || lastBank.BankAccountNo === "")) {
      toast.warn("Please fill all bank details including Type of Account and IFSC Code first");
      return;
    }

    const id = generateSixDigitNumber();
    setBankDetails([...bankDetails, {
      id, // This is a temporary ID for UI management
      bankId: null, // This will be null for new accounts
      IFSCCode: '',
      BankName: '',
      BankAccountNo: '',
      AccountType: '',
      useForRefund: false,
      isRefundAccount: false
    }]);

    // Clear bank-related errors when adding new bank
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith('bankName_') || key.startsWith('BankAccountNo_') || key.startsWith('IFSCCode_')) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  }

  // Update the removeBank function
  const removeBank = (id) => {
    // If it's a temporary ID (not from API), just remove from state
    const bankToRemove = bankDetails.find(bank => bank.id === id);

    if (bankToRemove && bankToRemove.bankId && bankToRemove.bankId !== null) {
      // If it has a valid bankId from API, call the delete API
      deleteBankAccount(bankToRemove.bankId);
    } else {
      // If it's a local temporary bank (including prefill data), just remove from state
      setBankDetails(bankDetails.filter((bank) => bank.id !== id));
    }
  }
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    middleName: '',
    lastName: '',
    fatherName: '',
    panNo: (localStorage.getItem("PAN_NO") || '').toUpperCase(),
    dateOfBirth: '',
    aadhaarNo: '',
    aadhaarEnrolmentId: '',
    // Contact Information
    countryCode: '',
    mobileNo: '',
    emailAddress: '',

    // Communication Address
    country: '',
    pinCode: '',
    townCity: '',
    state: '',
    flatDoor: '',
    nameOfPremises: '',
    roadStreet: '',
    area: '',
    natureOfEmployment: '',
    RoadOrStreet: '',
    LocalityOrArea: '',

    // Filing Section
    filedUnderSection: '',
    filledInResponse: '',
    receiptNo: '',
    dateOfFiling: '',
    uniqueNumber: '',
    dateOfNotice: '',

    // Bank Details
    ifscCode: '',
    bankName: '',
    accountNo: '',
    accountType: '',

    // status: 'Resident',
    status: 'Resident',
  })
  const [userStatus, setUserStatus] = useState('Resident')
  const [assesId, setAssesId] = useState('')
  const [addressId, setAddressId] = useState('')
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

  // Validation patterns
  const validationPatterns = {
    firstName: { maxLength: 25, required: true },
    middleName: { maxLength: 25 },
    lastName: { maxLength: 75, minLength: 1, required: true },
    panNo: { pattern: newValidationPatterns.PAN, required: true },
    flatDoor: { maxLength: 50, minLength: 1, required: true },
    nameOfPremises: { maxLength: 50 },
    RoadOrStreet: { maxLength: 50 },
    LocalityOrArea: { maxLength: 50, minLength: 1, required: true },
    townCity: { maxLength: 50, minLength: 1, required: true },
    pinCode: { pattern: newValidationPatterns.PIN, required: true },
    dateOfBirth: { pattern: /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))$/ },
    aadhaarNo: { pattern: newValidationPatterns.AADHAR },
    fatherName: { maxLength: 125, minLength: 1, required: true },
    // BankName: { maxLength: 125, minLength: 1, required: true },
    // BankAccountNo: { minLength: 1, required: true },
  };

  // Update the validateField function to handle undefined values
  const validateField = (name, value) => {
    const rules = validationPatterns[name];
    if (!rules) return '';

    let error = '';
    const stringValue = (value || '').trim();

    // Check required field
    if (rules.required && !stringValue) {
      error = 'This field is required';
    }
    // Check min length - only if field has some value
    else if (rules.minLength && stringValue.length > 0 && stringValue.length < rules.minLength) {
      error = `Minimum ${rules.minLength} characters required`;
    }
    // Check max length
    else if (rules.maxLength && stringValue.length > rules.maxLength) {
      error = `Maximum ${rules.maxLength} characters allowed`;
    }
    // Aadhaar special case: only show error if length is 12 and pattern does not match
    else if (name === 'aadhaarNo' && stringValue) {
      if (stringValue.length === 12 && !rules.pattern.test(stringValue)) {
        error = 'Invalid Aadhaar number format. Should be 12 digits.';
      }
    }
    // Generic pattern check for other fields - only if field has value
    else if (rules.pattern && stringValue && name !== 'aadhaarNo' && !rules.pattern.test(stringValue)) {
      error = 'Invalid format';
    }

    return error;
  };

  const handleChange = (e) => {
    markPageAsUnsaved('profile')
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate field on change and only set error if there's an actual error
    const error = validateField(name, value);
    setErrors(prev => {
      if (error) {
        return { ...prev, [name]: error };
      } else {
        // Remove error if field is now valid
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
    });
  };

  // Update the validateForm function to handle undefined values
  const validateForm = () => {
    const newErrors = {};

    // Validate all fields using validationPatterns
    Object.keys(validationPatterns).forEach(field => {
      if (validationPatterns[field].required) {
        const value = formData[field] || '';
        const error = validateField(field, value);
        if (error) newErrors[field] = error;
      }
    });

    // Additional required field validations that are not in validationPatterns
    if (!formData.countryCode?.trim()) newErrors.countryCode = 'Country code is required';
    if (!formData.mobileNo?.trim()) newErrors.mobileNo = 'Mobile number is required';
    if (!formData.emailAddress?.trim()) newErrors.emailAddress = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Invalid email format';
    }

    // Validate bank details with proper null checks
    bankDetails.forEach((bank, index) => {
      if (!bank?.BankName?.trim()) newErrors[`bankName_${index}`] = 'Bank name is required';
      if (!bank?.BankAccountNo?.trim()) newErrors[`BankAccountNo_${index}`] = 'Account number is required';
      if (!bank?.AccountType?.trim()) newErrors[`AccountType_${index}`] = 'Type of Account is required';
      if (!bank?.IFSCCode?.trim()) {
        newErrors[`IFSCCode_${index}`] = 'IFSC Code is required';
      } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bank.IFSCCode)) {
        newErrors[`IFSCCode_${index}`] = 'Invalid IFSC code format';
      }
    });

    // Validate that at least one bank account is marked as "use for refund"
    const hasRefundAccount = bankDetails.some(bank => bank.isRefundAccount === true);
    if (!hasRefundAccount) {
      newErrors.refundAccount = 'At least one bank account must be marked as "Use as Refund Account"';
    }

    return newErrors;
  };

  const [panCardNumber, setPanCardNumber] = useState((localStorage.getItem("PAN_NO") || '').toUpperCase())
  const [assessmentYear, setAssessmentYear] = useState(localStorage.getItem("YEAR"))
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      console.log("object form error", formErrors)
      setErrors(formErrors);
      toast.error('Please fix the validation errors');
      return;
    }

    // Debug logging for IDs
    console.log('Current assesId:', assesId);
    console.log('AssesId type:', typeof assesId);
    console.log('Current addressId:', addressId);
    console.log('AddressId type:', typeof addressId);

    const requestBody = {
      assessee: {
        // Don't send assesId to avoid entity identifier alteration issues
        // Let the backend handle assessee creation/update based on PAN number
        assessmentYear: assessmentYear, // Replace with actual value
        panNumber: panCardNumber.toUpperCase(),
        aadhaarCardNo: formData.aadhaarNo,
        firstName: formData.firstName,
        middleName: formData.middleName || "",
        surNameOrOrgName: formData.lastName || "",
        dob: formData.dateOfBirth,
        countryCodeMobile: formData.countryCode.replace('+', ''), // Remove + from country code
        mobileNo: parseInt(formData.mobileNo),
        emailAddress: formData.emailAddress,
        employerCategory: formData.natureOfEmployment,
        fatherName: formData.fatherName,
        residentialStatus: formData.status || "Resident", // add this to your formData
        address: {
          // Don't send addressId to avoid entity identifier alteration issues
          // Let the backend handle address creation/update based on assessee ID
          residenceNo: formData.flatDoor,
          residenceName: formData.nameOfPremises,
          localityOrArea: formData.LocalityOrArea,
          roadOrStreet: formData.RoadOrStreet,
          cityOrTownOrDistrict: formData.townCity,
          stateCode: formData.state,
          pinCode: parseInt(formData.pinCode),
          zipCode: formData.pinCode.toString(),
          countryCode: formData.country
        },
        banks: bankDetails.map((bank) => ({
          bankId: bank.bankId || null, // This will be null for new banks, existing ID for others
          ifscCode: bank.IFSCCode,
          bankName: bank.BankName,
          bankAccountNo: bank.BankAccountNo,
          accountType: bank.AccountType,
          useForRefund: bank.isRefundAccount,
        }))
      }
    };

    try {
      setIsSubmitting(true);
      
      // Debug logging for request body
      console.log('Request body being sent:', JSON.stringify(requestBody, null, 2));

      const response = await axios.patch(`${config.apiUrl}/user/profile/${panCardNumber}/${assessmentYear}`, requestBody).then((response) => {

        setIsSubmitting(false);
        markPageAsSaved('profile')
        
        // Clear only profile prefill data since this page is now saved
        localStorage.removeItem("prefill_profile");
        
        toast.success('Profile updated successfully!')
        fetchProfileData()
      }).catch((err) => {
        setIsSubmitting(false);
        console.log(err)
        toast.error(err.response.data.message)
      });


    } catch (error) {
      setIsSubmitting(false);
      toast.error('Failed to update profile. Please try again later.')
      console.error('Error:', error);
    }
  };

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedStateCode, setSelectedStateCode] = useState('');
  const [countries, setCountries] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  console.log("formData", formData)
  // Fetch countries on initial render
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);

  // Fetch states when selectedCountryCode changes
  useEffect(() => {
    if (selectedCountryCode) {
      const allStates = State.getStatesOfCountry(selectedCountryCode);
      setStates(allStates);
      setSelectedStateCode(''); // reset state & city
      setCities([]);
    }
  }, [selectedCountryCode]);

  // Fetch cities when selectedStateCode changes
  useEffect(() => {
    if (selectedCountryCode && selectedStateCode) {
      const allCities = City.getCitiesOfState(selectedCountryCode, selectedStateCode);
      setCities(allCities);
    }
  }, [selectedStateCode]);

  const fetchProfileData = async () => {
    try {
      await axios.get(`${config.apiUrl}/user/profile/${panCardNumber}/${assessmentYear}`).then((response) => {
        const data = response.data;
        
        // Debug logging for assesId from API
        const receivedAssesId = data?.assessee?.assesId || '';
        console.log('Received assesId from API:', receivedAssesId);
        console.log('AssesId type from API:', typeof receivedAssesId);
        setAssesId(receivedAssesId);
        
        // Debug logging for addressId from API
        const receivedAddressId = data?.assessee?.address?.addressId || '';
        console.log('Received addressId from API:', receivedAddressId);
        console.log('AddressId type from API:', typeof receivedAddressId);
        setAddressId(receivedAddressId);
        console.log("data.assessee.address.countryCodeMobile + data.assessee.mobileNo?.toString()", data.assessee?.countryCodeMobile + data.assessee.mobileNo?.toString())
        setFormData({
          firstName: data.assessee.firstName || '',
          middleName: data.assessee.middleName || '',
          lastName: data.assessee.surNameOrOrgName || '',
          flatDoor: data.assessee.address.residenceNo || '',
          nameOfPremises: data.assessee.address.residenceName || '',
          roadStreet: data.assessee.address.roadOrStreet || '',
          area: data.assessee.address.localityOrArea || '',
          townCity: data.assessee.address.cityOrTownOrDistrict || '',
          state: data.assessee.address.stateCode || '',
          RoadOrStreet: data.assessee.address.roadOrStreet || '',
          LocalityOrArea: data.assessee.address.localityOrArea || '',
          country: data.assessee.address.countryCode || 'India',
          pinCode: data.assessee.address.pinCode?.toString() || '',
          countryCode: data.assessee?.countryCodeMobile ? `+${data.assessee.countryCodeMobile}` : '+91',
          mobileNo: data.assessee.mobileNo?.toString() || '',
          emailAddress: data.assessee.emailAddress || '',
          panNo: (data.assessee.panNumber || '').toUpperCase(),
          dateOfBirth: data.assessee.dob || '',
          natureOfEmployment: data.assessee.employerCategory || '',
          aadhaarNo: data.assessee.aadhaarCardNo || '',
          fatherName: data.assessee.fatherName || '',
          residentialStatus: data.assessee.residentialStatus || '',
        });

        setSelectedCountryCode(data.assessee.address.countryCode || 'IN');

        // Handle empty bank details - ensure all fields have proper defaults
        const bankData = data?.assessee?.banks || [];
        if (bankData.length === 0) {
          // If no bank data, initialize with empty bank
          setBankDetails([{
            id: 1,
            IFSCCode: '',
            BankName: '',
            BankAccountNo: '',
            AccountType: '',
            useForRefund: false,
            isRefundAccount: false
          }]);
        } else {
          // Map API data with proper defaults for empty values
          setBankDetails(
            bankData.map((bank) => ({
              id: bank.bankId || generateSixDigitNumber(),
              IFSCCode: bank.ifscCode || '',
              BankName: bank.bankName || '',
              BankAccountNo: bank.bankAccountNo || '',
              AccountType: bank.accountType || '',
              bankId: bank.bankId || null,
              isRefundAccount: bank.useForRefund === true || bank.useForRefund === 'true'
            }))
          );
        }

        setSelectedStateCode(data.assessee.address.stateCode || '');
        setSelectedCity(data.assessee.address.cityOrTownOrDistrict || '');

      }).catch((error) => {
        // If API fails, initialize with empty bank
        setBankDetails([{
          id: 1,
          IFSCCode: '',
          BankName: '',
          BankAccountNo: '',
          AccountType: '',
          useForRefund: false,
          isRefundAccount: false
        }]);
      });
    } catch (error) {
      toast.error('Failed to fetch profile data.');
      // If API fails, initialize with empty bank
      setBankDetails([{
        id: 1,
        IFSCCode: '',
        BankName: '',
        BankAccountNo: '',
        AccountType: '',
        useForRefund: false,
        isRefundAccount: false
      }]);
    }
  };

  useEffect(() => {
    setAssessmentYear(localStorage.getItem("YEAR"));
    setPanCardNumber(localStorage.getItem("PAN_NO"));
    
    // Ensure PAN field is always synchronized with localStorage
    setFormData(prev => ({
      ...prev,
      panNo: (localStorage.getItem("PAN_NO") || prev.panNo).toUpperCase()
    }));
  }, [])

  useEffect(() => {
    // Check if prefill data exists - if yes, load it and skip API call
    const prefillProfile = localStorage.getItem("prefill_profile");
    
    if (prefillProfile) {
      console.log('Prefill profile data found - loading from localStorage');
      try {
        const profileData = JSON.parse(prefillProfile);
        setFormData(prev => ({
          ...prev,
          firstName: profileData.firstName || prev.firstName,
          middleName: profileData.middleName || prev.middleName,
          lastName: profileData.lastName || prev.lastName,
          fatherName: profileData.fatherName || prev.fatherName,
          dateOfBirth: profileData.dob || prev.dateOfBirth,
          // PAN is always kept from localStorage, never updated from prefill data
          panNo: (localStorage.getItem("PAN_NO") || prev.panNo).toUpperCase()
        }));

        // Handle address details from prefill data
        if (profileData.addressDetails) {
          const addressInfo = profileData.addressDetails;
          console.log('Loading prefill address details:', addressInfo);
          setFormData(prev => ({
            ...prev,
            flatDoor: String(addressInfo.residenceNo || prev.flatDoor || ''),
            roadStreet: String(addressInfo.roadOrStreet || prev.roadStreet || ''),
            RoadOrStreet: String(addressInfo.roadOrStreet || prev.RoadOrStreet || ''),
            area: String(addressInfo.localityOrArea || prev.area || ''),
            LocalityOrArea: String(addressInfo.localityOrArea || prev.LocalityOrArea || ''),
            townCity: String(addressInfo.cityOrTownOrDistrict || prev.townCity || ''),
            state: String(addressInfo.stateCode || prev.state || ''),
            pinCode: String(addressInfo.pinCode || prev.pinCode || ''),
            zipCode: String(addressInfo.zipCode || prev.zipCode || ''),
            country: String(addressInfo.countryCode || prev.country || ''),
            mobileNo: String(addressInfo.mobileNo || prev.mobileNo || ''),
            countryCodeMobile: String(addressInfo.countryCodeMobile || prev.countryCodeMobile || ''),
            emailAddress: String(addressInfo.emailAddress || prev.emailAddress || ''),
            emailAddressSecondary: String(addressInfo.emailAddressSecondary || prev.emailAddressSecondary || ''),
            nameOfPremises: String(addressInfo.residenceName || prev.nameOfPremises || '')
          }));
          
          // Update country code state for the dropdown
          if (addressInfo.countryCode) {
            setSelectedCountryCode(addressInfo.countryCode);
          }
        }

        // Handle bank details from prefill data
        if (profileData.bankDetails) {
          const bankInfo = profileData.bankDetails;
          console.log('Loading prefill bank details:', bankInfo);
          const newBankDetail = {
            id: generateSixDigitNumber(), // Generate unique ID for prefill bank
            bankId: null, // New bank, no API ID yet
            BankName: String(bankInfo.bankName || ''),
            IFSCCode: String(bankInfo.ifscCode || ''),
            BankAccountNo: String(bankInfo.accountNumber || ''),
            AccountType: String(bankInfo.accountType || 'SB'),
            useForRefund: bankInfo.useForRefund || false,
            isRefundAccount: bankInfo.useForRefund || false
          };
          console.log('Mapped bank detail:', newBankDetail);
          console.log('useForRefund value from JSON:', bankInfo.useForRefund);
          console.log('isRefundAccount set to:', newBankDetail.isRefundAccount);

          // Add bank details to the list
          setBankDetails(prev => {
            // Check if bank already exists (by IFSC code)
            const existingBank = prev.find(bank => bank.IFSCCode === bankInfo.ifscCode);
            let updatedBanks;
            
            if (existingBank) {
              // Update existing bank
              updatedBanks = prev.map(bank => 
                bank.IFSCCode === bankInfo.ifscCode 
                  ? { ...bank, ...newBankDetail }
                  : bank
              );
            } else {
              // Check if there's an empty bank (first bank with no data) to replace
              const emptyBankIndex = prev.findIndex(bank => 
                !bank.IFSCCode && !bank.BankName && !bank.BankAccountNo && !bank.AccountType
              );
              
              if (emptyBankIndex !== -1) {
                // Replace the empty bank with prefill data
                updatedBanks = prev.map((bank, index) => 
                  index === emptyBankIndex ? newBankDetail : bank
                );
              } else {
                // Add new bank if no empty bank found
                updatedBanks = [...prev, newBankDetail];
              }
            }
            
            // If this bank is marked as refund account, uncheck all other banks
            if (newBankDetail.isRefundAccount) {
              updatedBanks = updatedBanks.map(bank => 
                bank.IFSCCode === bankInfo.ifscCode 
                  ? bank 
                  : { ...bank, isRefundAccount: false }
              );
            }
            
            return updatedBanks;
          });
        }

        console.log('Loaded prefill profile data from localStorage');
      } catch (error) {
        console.error('Error loading prefill profile data:', error);
        // If prefill fails, fetch from API
    fetchProfileData();
      }
    } else {
      // No prefill data - fetch from API
      console.log('No prefill profile data found - fetching from API');
      fetchProfileData();
    }
  }, []);

  // Listen for prefill data updates from project-level uploader
  useEffect(() => {
    const handlePrefillDataUpdate = (event) => {
      const prefillData = event.detail;
      
      if (prefillData.profile) {
        const profile = prefillData.profile;
        
        // Always replace current data with prefill data when uploaded
        setFormData(prev => ({
          ...prev,
          firstName: profile.firstName || prev.firstName,
          middleName: profile.middleName || prev.middleName,
          lastName: profile.lastName || prev.lastName,
          fatherName: profile.fatherName || prev.fatherName,
          dateOfBirth: profile.dob || prev.dateOfBirth,
          // PAN is always kept from localStorage, never updated from prefill data
          panNo: (localStorage.getItem("PAN_NO") || prev.panNo).toUpperCase()
        }));

        // Handle address details from prefill data
        if (profile.addressDetails) {
          const addressInfo = profile.addressDetails;
          console.log('Received prefill address details update:', addressInfo);
          setFormData(prev => ({
            ...prev,
            flatDoor: String(addressInfo.residenceNo || prev.flatDoor || ''),
            roadStreet: String(addressInfo.roadOrStreet || prev.roadStreet || ''),
            RoadOrStreet: String(addressInfo.roadOrStreet || prev.RoadOrStreet || ''),
            area: String(addressInfo.localityOrArea || prev.area || ''),
            LocalityOrArea: String(addressInfo.localityOrArea || prev.LocalityOrArea || ''),
            townCity: String(addressInfo.cityOrTownOrDistrict || prev.townCity || ''),
            state: String(addressInfo.stateCode || prev.state || ''),
            pinCode: String(addressInfo.pinCode || prev.pinCode || ''),
            zipCode: String(addressInfo.zipCode || prev.zipCode || ''),
            country: String(addressInfo.countryCode || prev.country || ''),
            mobileNo: String(addressInfo.mobileNo || prev.mobileNo || ''),
            countryCodeMobile: String(addressInfo.countryCodeMobile || prev.countryCodeMobile || ''),
            emailAddress: String(addressInfo.emailAddress || prev.emailAddress || ''),
            emailAddressSecondary: String(addressInfo.emailAddressSecondary || prev.emailAddressSecondary || ''),
            nameOfPremises: String(addressInfo.residenceName || prev.nameOfPremises || '')
          }));
          
          // Update country code state for the dropdown
          if (addressInfo.countryCode) {
            setSelectedCountryCode(addressInfo.countryCode);
          }
        }

        // Handle bank details from prefill data
        if (profile.bankDetails) {
          const bankInfo = profile.bankDetails;
          console.log('Received prefill bank details update:', bankInfo);
          const newBankDetail = {
            id: generateSixDigitNumber(), // Generate unique ID for prefill bank
            bankId: null, // New bank, no API ID yet
            BankName: String(bankInfo.bankName || ''),
            IFSCCode: String(bankInfo.ifscCode || ''),
            BankAccountNo: String(bankInfo.accountNumber || ''),
            AccountType: String(bankInfo.accountType || 'SB'),
            useForRefund: bankInfo.useForRefund || false,
            isRefundAccount: bankInfo.useForRefund || false
          };
          console.log('Mapped bank detail for update:', newBankDetail);
          console.log('useForRefund value from JSON update:', bankInfo.useForRefund);
          console.log('isRefundAccount set to:', newBankDetail.isRefundAccount);

          // Add bank details to the list
          setBankDetails(prev => {
            // Check if bank already exists (by IFSC code)
            const existingBank = prev.find(bank => bank.IFSCCode === bankInfo.ifscCode);
            let updatedBanks;
            
            if (existingBank) {
              // Update existing bank
              updatedBanks = prev.map(bank => 
                bank.IFSCCode === bankInfo.ifscCode 
                  ? { ...bank, ...newBankDetail }
                  : bank
              );
            } else {
              // Check if there's an empty bank (first bank with no data) to replace
              const emptyBankIndex = prev.findIndex(bank => 
                !bank.IFSCCode && !bank.BankName && !bank.BankAccountNo && !bank.AccountType
              );
              
              if (emptyBankIndex !== -1) {
                // Replace the empty bank with prefill data
                updatedBanks = prev.map((bank, index) => 
                  index === emptyBankIndex ? newBankDetail : bank
                );
              } else {
                // Add new bank if no empty bank found
                updatedBanks = [...prev, newBankDetail];
              }
            }
            
            // If this bank is marked as refund account, uncheck all other banks
            if (newBankDetail.isRefundAccount) {
              updatedBanks = updatedBanks.map(bank => 
                bank.IFSCCode === bankInfo.ifscCode 
                  ? bank 
                  : { ...bank, isRefundAccount: false }
              );
            }
            
            return updatedBanks;
          });

          if (profile.addressDetails) {
            toast.success('Profile, address, and bank data updated from prefill data');
          } else {
            toast.success('Profile and bank data updated from prefill data');
          }
        } else if (profile.addressDetails) {
          toast.success('Profile and address data updated from prefill data');
        } else {
          toast.success('Profile data updated from prefill data');
        }
      }
    };

    window.addEventListener('prefillDataUpdated', handlePrefillDataUpdate);
    
    return () => {
      window.removeEventListener('prefillDataUpdated', handlePrefillDataUpdate);
    };
  }, []);

  // Update the deleteBankAccount function
  const deleteBankAccount = async (bankId) => {
    try {
      if (!bankId) {
        // If no bankId, it's a local temporary bank - just remove from state
        setBankDetails(bankDetails.filter((bank) => bank.bankId !== bankId));
        return;
      }

      setIsSubmitting(true);
      await axios.delete(`${config.apiUrl}/user/profile/bank/${bankId}`);

      setIsSubmitting(false);
      toast.success('Bank removed successfully!');

      // Remove the bank from local state
      setBankDetails(bankDetails.filter((bank) => bank.bankId !== bankId));
    } catch (error) {
      setIsSubmitting(false);
      toast.error('Something went wrong!');
      console.error(error);
    }
  }


  const status = [
    "Resident",
    "Non-Resident",
    "Not Ordinarily Resident",
  ]

  useEffect(() => {
    localStorage.setItem('firstName', formData.firstName)
    localStorage.setItem('lastName', formData.lastName)
  }, [formData])

  const [countriesList, setCountriesList] = useState([])
  const [statesList, setStatesList] = useState([])
  const [accountType, setAccountType] = useState([])

  const getAllCodeList = async (codeType, setDataFn) => {
    try {
      const response = await axios.get(`${config.apiUrl}/system/code/${codeType}`);
      const data = response.data;

      if (data && data.codes?.length > 0) {
        // Sort the codes by description in ascending order
        const sortedCodes = data.codes.sort((a, b) => {
          return a.description.localeCompare(b.description);
        });
        setDataFn(sortedCodes);
      } else {
        setDataFn([]); // empty array if no data
      }
    } catch (error) {
      console.log(`Error fetching ${codeType}:`, error);
      toast.error(`Failed to fetch ${codeType}.`);
    }
  };

  useEffect(() => {
    getAllCodeList("CountryCode", setCountriesList);
    getAllCodeList("StateCode", setStatesList);
    getAllCodeList("AccountType", setAccountType);
  }, [])

  // Debug: Log accountType data
  useEffect(() => {
    console.log('AccountType data loaded:', accountType);
  }, [accountType])

  return (
    <Dashboard>
      <ToastContainer />
      <div className="min-h-screen bg-gray-50 py-1 px-1">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full"
        >
          <div className="bg-white rounded-lg shadow-lg p-2 md:p-3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900"></h2>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white transition-colors ${isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700'
                  }`}
              >
                {isSubmitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                {isSubmitting ? 'Saving...' : 'Update Profile'}
              </motion.button>
            </div>

            {showSuccessMessage && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700"
              >
                Profile updated successfully!
              </motion.div>
            )}

            <motion.form 
              className="space-y-4"
              layout
            >
              {/* Personal Information - Custom Layout */}
              <div className="space-y-2">
                <h3 className="text-base font-semibold text-cyan-800 border-b border-gray-200 pb-1">
                  Personal Information
                </h3>
                
                {/* First Row: PAN No. and Date of Birth */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">
                      PAN No. <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="panNo"
                      disabled={true}
                      value={formData.panNo}
                      onChange={handleChange}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-not-allowed${errors.panNo ? 'border-red-500' : 'border-gray-300'
                        }`}
                      title="PAN number is automatically loaded from your account and cannot be changed"
                    />
                    {errors.panNo && (
                      <p className="text-red-500 text-xs mt-1">{errors.panNo}</p>
                    )}
                  </div>

                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.dateOfBirth && (
                      <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>
                    )}
                  </div>
                </div>

                {/* Second Row: First Name, Middle Name, Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      maxLength={25}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.firstName ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                    )}
                  </div>

                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">
                      Middle Name
                    </label>
                    <input
                      type="text"
                      name="middleName"
                      value={formData.middleName}
                      onChange={handleChange}
                      maxLength={25}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.middleName ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.middleName && (
                      <p className="text-red-500 text-xs mt-1">{errors.middleName}</p>
                    )}
                  </div>

                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      maxLength={75}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.lastName ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                {/* Third Row: Father's Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">
                      Father's Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="fatherName"
                      value={formData.fatherName}
                      onChange={handleChange}
                      maxLength={125}
                      className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.fatherName ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.fatherName && (
                      <p className="text-red-500 text-xs mt-1">{errors.fatherName}</p>
                    )}
                  </div>
                </div>
              </div>

              <Section title="Additional Personal Information">

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">
                    Aadhaar No
                  </label>
                  <input
                    type="text"
                    name="aadhaarNo"
                    value={formData.aadhaarNo}
                    onChange={handleChange}
                    maxLength={12}
                    className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.aadhaarNo ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.aadhaarNo && (
                    <p className="text-red-500 text-xs mt-1">{errors.aadhaarNo}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1" htmlFor="city">
                    Status
                  </label>
                  <div className="relative">
                    <select
                      onChange={(e) => {
                        setFormData({ ...formData, status: e.target.value })
                      }}
                      value={formData.status}
                      className="w-full p-2 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      {status?.map((e) => (
                        <option key={e} value={e}>
                          {e}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-3.5 text-cyan-500 pointer-events-none" size={20} />
                  </div>
                </div>
              </Section>

              <Section title="Contact Information">
                {/* Country Code and Mobile Number */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country Code <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select Country Code</option>
                      <option value="+213">+213 (Algeria)</option>
                      <option value="+244">+244 (Angola)</option>
                      <option value="+297">+297 (Aruba)</option>
                      <option value="+61">+61 (Australia)</option>
                      <option value="+229">+229 (Benin)</option>
                      <option value="+55">+55 (Brazil)</option>
                      <option value="+246">+246 (British Indian Ocean Territory)</option>
                      <option value="+226">+226 (Burkina Faso)</option>
                      <option value="+257">+257 (Burundi)</option>
                      <option value="+237">+237 (Cameroon)</option>
                      <option value="+238">+238 (Cape Verde)</option>
                      <option value="+235">+235 (Chad)</option>
                      <option value="+86">+86 (China)</option>
                      <option value="+242">+242 (Republic of the Congo)</option>
                      <option value="+243">+243 (Democratic Republic of the Congo)</option>
                      <option value="+269">+269 (Comoros)</option>
                      <option value="+253">+253 (Djibouti)</option>
                      <option value="+20">+20 (Egypt)</option>
                      <option value="+240">+240 (Equatorial Guinea)</option>
                      <option value="+291">+291 (Eritrea)</option>
                      <option value="+251">+251 (Ethiopia)</option>
                      <option value="+298">+298 (Faroe Islands)</option>
                      <option value="+33">+33 (France)</option>
                      <option value="+241">+241 (Gabon)</option>
                      <option value="+220">+220 (Gambia)</option>
                      <option value="+49">+49 (Germany)</option>
                      <option value="+233">+233 (Ghana)</option>
                      <option value="+299">+299 (Greenland)</option>
                      <option value="+224">+224 (Guinea)</option>
                      <option value="+245">+245 (Guinea-Bissau)</option>
                      <option value="+225">+225 (Ivory Coast)</option>
                      <option value="+91">+91 (India)</option>
                      <option value="+62">+62 (Indonesia)</option>
                      <option value="+39">+39 (Italy)</option>
                      <option value="+81">+81 (Japan)</option>
                      <option value="+254">+254 (Kenya)</option>
                      <option value="+82">+82 (South Korea)</option>
                      <option value="+231">+231 (Liberia)</option>
                      <option value="+218">+218 (Libya)</option>
                      <option value="+261">+261 (Madagascar)</option>
                      <option value="+265">+265 (Malawi)</option>
                      <option value="+60">+60 (Malaysia)</option>
                      <option value="+223">+223 (Mali)</option>
                      <option value="+262">+262 (Mayotte)</option>
                      <option value="+52">+52 (Mexico)</option>
                      <option value="+212">+212 (Morocco)</option>
                      <option value="+258">+258 (Mozambique)</option>
                      <option value="+264">+264 (Namibia)</option>
                      <option value="+227">+227 (Niger)</option>
                      <option value="+234">+234 (Nigeria)</option>
                      <option value="+63">+63 (Philippines)</option>
                      <option value="+7">+7 (Russia)</option>
                      <option value="+250">+250 (Rwanda)</option>
                      <option value="+239">+239 (São Tomé and Príncipe)</option>
                      <option value="+966">+966 (Saudi Arabia)</option>
                      <option value="+221">+221 (Senegal)</option>
                      <option value="+248">+248 (Seychelles)</option>
                      <option value="+232">+232 (Sierra Leone)</option>
                      <option value="+65">+65 (Singapore)</option>
                      <option value="+252">+252 (Somalia)</option>
                      <option value="+27">+27 (South Africa)</option>
                      <option value="+34">+34 (Spain)</option>
                      <option value="+249">+249 (Sudan)</option>
                      <option value="+66">+66 (Thailand)</option>
                      <option value="+228">+228 (Togo)</option>
                      <option value="+216">+216 (Tunisia)</option>
                      <option value="+90">+90 (Turkey)</option>
                      <option value="+256">+256 (Uganda)</option>
                      <option value="+971">+971 (UAE)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+1">+1 (USA)</option>
                      <option value="+84">+84 (Vietnam)</option>
                      <option value="+260">+260 (Zambia)</option>
                      <option value="+263">+263 (Zimbabwe)</option>
                    </select>
                    {errors.countryCode && (
                      <p className="text-red-500 text-xs mt-1">{errors.countryCode}</p>
                    )}
                  </div>
                  
                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile No. <span className="text-red-500">*</span>
                  </label>
                    <input
                      type="tel"
                      name="mobileNo"
                    value={formData.mobileNo}
                      onChange={handleChange}
                      placeholder="Enter mobile number"
                      className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      required
                  />
                  {errors.mobileNo && (
                    <p className="text-red-500 text-xs mt-1">{errors.mobileNo}</p>
                  )}
                  </div>
                </div>

                {/* Email Address */}
                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.emailAddress ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.emailAddress && (
                    <p className="text-red-500 text-xs mt-1">{errors.emailAddress}</p>
                  )}
                </div>
              </Section>

              <Section title="Communication Address">
                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1" htmlFor="country">
                    Select Country
                  </label>
                  <div className="relative">
                    <select
                      onChange={(e) => {
                        const selectedCode = e.target.value;
                        setSelectedCountryCode(selectedCode);
                        setFormData({ ...formData, country: selectedCode || '' });
                      }}
                      value={selectedCountryCode}
                      className="w-full px-3 py-2 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="">Select Country</option>
                      {countriesList.map((country) => (
                        <option key={country.codeValue} value={country.codeValue}>
                          {country.description}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-3.5 text-cyan-500 pointer-events-none" size={20} />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1" htmlFor="state">
                    Select State
                  </label>
                  <div className="relative">
                    <select
                      onChange={(e) => {
                        setFormData({ ...formData, state: e.target.value })
                      }}
                      value={formData.state}
                      className="w-full px-3 py-2 border appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option>Select State</option>
                      {statesList?.map((state) => (
                        <option key={state.codeValue} value={state.codeValue}>
                          {state.description}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-3.5 text-cyan-500 pointer-events-none" size={20} />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">
                    Town/City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="townCity"
                    value={formData.townCity}
                    onChange={handleChange}
                    maxLength={50}
                    className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.townCity ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.townCity && (
                    <p className="text-red-500 text-xs mt-1">{errors.townCity}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">
                    Flat / Door / Block No. <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="flatDoor"
                    value={formData.flatDoor}
                    onChange={handleChange}
                    maxLength={50}
                    className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.flatDoor ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.flatDoor && (
                    <p className="text-red-500 text-xs mt-1">{errors.flatDoor}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">
                    Name of Premises / Building
                  </label>
                  <input
                    type="text"
                    name="nameOfPremises"
                    value={formData.nameOfPremises}
                    onChange={handleChange}
                    maxLength={50}
                    className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.nameOfPremises ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.nameOfPremises && (
                    <p className="text-red-500 text-xs mt-1">{errors.nameOfPremises}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">
                    Road / Street
                  </label>
                  <input
                    type="text"
                    name="RoadOrStreet"
                    value={formData.RoadOrStreet}
                    onChange={handleChange}
                    maxLength={50}
                    className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.RoadOrStreet ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.RoadOrStreet && (
                    <p className="text-red-500 text-xs mt-1">{errors.RoadOrStreet}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">
                    Locality / Area <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="LocalityOrArea"
                    value={formData.LocalityOrArea}
                    onChange={handleChange}
                    maxLength={50}
                    className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.LocalityOrArea ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.LocalityOrArea && (
                    <p className="text-red-500 text-xs mt-1">{errors.LocalityOrArea}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">
                    PIN / ZIP Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="pinCode"
                    value={formData.pinCode}
                    onChange={handleChange}
                    maxLength={6}
                    className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.pinCode ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {errors.pinCode && (
                    <p className="text-red-500 text-xs mt-1">{errors.pinCode}</p>
                  )}
                </div>
              </Section>

              <motion.div
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                layout
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-thin text-cyan-800">Bank Account Details</h3>
                  <button
                    type="button"
                    onClick={addBank}
                    className="flex items-center gap-2 text-cyan-500 hover:text-cyan-600"
                  >
                    <Plus size={20} /> Add Bank
                  </button>
                </div>
                <hr />

                {bankDetails.map((data, index) => {
                  // Ensure data has all required properties with defaults
                  const bankData = {
                    id: data?.id || index + 1,
                    IFSCCode: data?.IFSCCode || '',
                    BankName: data?.BankName || '',
                    BankAccountNo: data?.BankAccountNo || '',
                    AccountType: data?.AccountType || '',
                    isRefundAccount: data?.isRefundAccount || false
                  };

                  return (
                    <motion.div
                      key={bankData.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      layout
                      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 items-start border p-2 rounded-lg mb-2 shadow-sm items-center"
                    >
                      {/* Name of Bank */}
                      <div className="w-full">
                        <label className="block text-sm text-gray-600 mb-1">
                          Name of Bank <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={bankData.BankName}
                          placeholder="Name of Bank"
                          className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors[`bankName_${index}`] ? 'border-red-500' : 'border-gray-300'
                            }`}
                          onChange={(e) => {
                            const value = e.target.value;
                            setBankDetails((prev) =>
                              prev.map((bank) =>
                                bank.id === bankData.id ? { ...bank, BankName: value } : bank
                              )
                            );
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              const key = `bankName_${index}`;
                              if (value && value.trim().length > 0) {
                                delete newErrors[key];
                              } else {
                                newErrors[key] = 'Bank name is required';
                              }
                              return newErrors;
                            });
                          }}
                        />
                        {errors[`bankName_${index}`] && (
                          <p className="text-red-500 text-xs mt-1">{errors[`bankName_${index}`]}</p>
                        )}
                      </div>

                      {/* Type of Account */}
                      <div className="w-full">
                        <label className="block text-sm text-gray-600 mb-1">Type of Account <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <select
                            value={bankData.AccountType}
                            onChange={(e) =>
                              setBankDetails((prev) =>
                                prev.map((bank) =>
                                  bank.id === bankData.id ? { ...bank, AccountType: e.target.value } : bank
                                )
                              )
                            }
                            className={`w-full p-2 border appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors[`AccountType_${index}`] ? 'border-red-500' : 'border-gray-300'}`}
                          >
                            <option value="">Select Account Type</option>
                            {accountType?.map((type) => (
                              <option key={type.codeValue} value={type.codeValue}>
                                {type.description}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-3 text-cyan-500 pointer-events-none" size={20} />
                        </div>
                        {errors[`AccountType_${index}`] && (
                          <p className="text-red-500 text-xs mt-1">{errors[`AccountType_${index}`]}</p>
                        )}
                      </div>

                      {/* IFSC Code */}
                      <div className="w-full">
                        <label className="block text-sm text-gray-600 mb-1">IFSC Code <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={bankData.IFSCCode}
                          placeholder="IFSC Code"
                          className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors[`IFSCCode_${index}`] ? 'border-red-500' : 'border-gray-300'
                            }`}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            setBankDetails((prev) =>
                              prev.map((bank) =>
                                bank.id === bankData.id ? { ...bank, IFSCCode: value } : bank
                              )
                            );
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              const key = `IFSCCode_${index}`;
                              if (!value || value.trim().length === 0) {
                                newErrors[key] = 'IFSC Code is required';
                              } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
                                newErrors[key] = 'Invalid IFSC code format';
                              } else {
                                delete newErrors[key];
                              }
                              return newErrors;
                            });
                          }}
                        />
                        {errors[`IFSCCode_${index}`] && (
                          <p className="text-red-500 text-xs mt-1">{errors[`IFSCCode_${index}`]}</p>
                        )}
                      </div>

                      {/* Account Number + Delete Button */}
                      <div className="w-full flex flex-col">
                        <label className="block text-sm text-gray-600 mb-1">
                          Account No. <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={bankData.BankAccountNo}
                            placeholder="Account No."
                            className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors[`BankAccountNo_${index}`] ? 'border-red-500' : 'border-gray-300'
                              }`}
                            onChange={(e) => {
                              const value = e.target.value;
                              setBankDetails((prev) =>
                                prev.map((bank) =>
                                  bank.id === bankData.id ? { ...bank, BankAccountNo: value } : bank
                                )
                              );
                              setErrors((prev) => {
                                const newErrors = { ...prev };
                                const key = `BankAccountNo_${index}`;
                                if (value && value.trim().length > 0) {
                                  delete newErrors[key];
                                } else {
                                  newErrors[key] = 'Account number is required';
                                }
                                return newErrors;
                              });
                            }}
                          />
                          {bankDetails.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeBank(bankData.id)} // Use removeBank instead of deleteBankAccount
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 size={20} />
                            </button>
                          )}
                        </div>
                        {errors[`BankAccountNo_${index}`] && (
                          <p className="text-red-500 text-xs mt-1">{errors[`BankAccountNo_${index}`]}</p>
                        )}
                      </div>

                      {/* Refund Account Checkbox */}

                      <div className="w-full flex items-center gap-2 pt-6 sm:pt-0">
                        <input
                          type="checkbox"
                          checked={bankData.isRefundAccount || false}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setBankDetails((prev) =>
                              prev.map((bank) =>
                                bank.id === bankData.id
                                  ? { ...bank, isRefundAccount: isChecked }
                                  : { ...bank, isRefundAccount: false } // Uncheck all other banks
                              )
                            );
                          }}
                          className="w-5 h-5 text-cyan-500 border-gray-300 rounded focus:ring-2 focus:ring-cyan-500"
                        />
                        <label className="text-sm text-gray-700">Use as Refund Account</label>
                      </div>
                    </motion.div>
                  );
                })}

                {/* Refund Account Validation Error */}
                {errors.refundAccount && (
                  <div className="col-span-full">
                    <p className="text-red-500 text-sm mt-2">{errors.refundAccount}</p>
                  </div>
                )}

              </motion.div>
            </motion.form>
          </div>
        </motion.div>
      </div>

      <ValidationErrorSidebar
        errors={errors}
        isVisible={Object.keys(errors).length > 0}
        onFieldClick={(fieldKey) => {
          const inputElement = document.querySelector(`input[name="${fieldKey}"]`);
          if (inputElement) {
            inputElement.focus();
            inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
        title="Profile Validation Errors"
      />
    </Dashboard>
  );
}